import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import client from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/StatusBadge';
import { FmcsaWidget } from '@/components/FmcsaWidget';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from 'recharts';
import { Truck, FileText, ClipboardCheck, AlertOctagon, GraduationCap, ShieldCheck, Lock, Package, IdCard } from 'lucide-react';

const GREEN = '#16A34A', RED = '#DC2626';

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { client.get('/dashboard/driver').then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  if (d === null) return <AppShell title="My Dashboard"><div className="grid gap-4 md:grid-cols-3">{[1,2,3].map(i=><Skeleton key={i} className="h-28" />)}</div></AppShell>;
  if (d === false) return (
    <AppShell title="My Dashboard">
      <Card className="p-8 text-center">
        <IdCard className="mx-auto mb-3 h-10 w-10 text-[hsl(var(--primary))]" />
        <div className="font-display text-lg font-semibold">Let's set up your driver profile</div>
        <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Complete your driver information to get started with onboarding.</p>
        <Button className="mt-4" onClick={() => navigate('/my-info')} data-testid="driver-setup-button">Complete My Information</Button>
      </Card>
    </AppShell>
  );

  const c = d.counts;
  const comp = d.driver.completion;
  const inspData = [
    { name: 'Clean', value: c.inspections - c.inspections_with_violations, color: GREEN },
    { name: 'With Violations', value: c.inspections_with_violations, color: RED },
  ];
  const KPIS = [
    { l: 'My Documents', v: c.documents, i: FileText, color: '#2563EB' },
    { l: 'My Truck', v: c.vehicles, i: Truck, color: '#F5B301' },
    { l: 'Inspections', v: c.inspections, i: ClipboardCheck, color: '#0EA5E9' },
    { l: 'Violations', v: c.violations, i: AlertOctagon, color: '#DC2626' },
  ];

  return (
    <AppShell title="My Dashboard">
      {/* status banner */}
      <div className={`mb-5 flex items-center gap-3 rounded-xl border p-4 ${d.driver.shipping_unlocked ? 'border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/5' : 'border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger))]/5'}`}>
        {d.driver.shipping_unlocked ? <ShieldCheck className="h-6 w-6 text-[hsl(var(--success))]" /> : <Lock className="h-6 w-6 text-[hsl(var(--danger))]" />}
        <div className="flex-1">
          <div className="font-display font-semibold" data-testid="driver-dashboard-name">{d.driver.name} · {d.driver.license_type}</div>
          <div className="text-sm text-muted-foreground">{d.driver.shipping_unlocked ? 'You are cleared for shipping & loads.' : 'Shipping locked until all required documents are approved.'}</div>
        </div>
        <StatusBadge status={d.driver.compliance?.status} />
      </div>

      {/* profile completion banner */}
      {comp && !comp.complete && (
        <div className="mb-5 flex flex-col gap-3 rounded-xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/5 p-4 sm:flex-row sm:items-center" data-testid="driver-completion-banner">
          <IdCard className="h-6 w-6 shrink-0 text-[hsl(var(--gold))]" />
          <div className="flex-1">
            <div className="font-medium">Complete your driver information ({comp.percent}%)</div>
            <Progress value={comp.percent} className="mt-2 h-2" />
          </div>
          <Button size="sm" onClick={() => navigate('/my-info')} data-testid="driver-complete-info-button">Complete now</Button>
        </div>
      )}

      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {KPIS.map((k) => { const Ic = k.i; return (
          <Card key={k.l} className="flex items-center gap-3 p-4"><div className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `${k.color}1a` }}><Ic className="h-5 w-5" style={{ color: k.color }} /></div><div><div className="text-xs uppercase tracking-wide text-muted-foreground">{k.l}</div><div className="font-display text-2xl font-bold tabular-nums">{k.v}</div></div></Card>
        ); })}
      </div>

      <div className="mb-5 grid gap-4 lg:grid-cols-3">
        <FmcsaWidget readOnly />
        <Card className="p-5">
          <div className="mb-2 font-display font-semibold">My Inspections</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart><Pie data={inspData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70}>{inspData.map((e, i) => <Cell key={i} fill={e.color} />)}</Pie><Tooltip /><Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} /></PieChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <div className="mb-3 font-display font-semibold">My Assigned Truck</div>
          {d.vehicles.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No truck assigned.</p> : d.vehicles.map((v) => (
            <div key={v.vehicle_id} className="flex items-center justify-between border-b py-3 last:border-0">
              <div className="flex items-center gap-3"><Truck className="h-5 w-5 text-[hsl(var(--gold))]" /><div><div className="font-medium">#{v.unit_number} · {v.make} {v.model} ({v.year})</div><div className="text-xs text-muted-foreground">VIN …{v.vin?.slice(-6)} · Plate {v.plate}</div></div></div>
              <StatusBadge status={v.status} />
            </div>
          ))}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2 font-display font-semibold"><GraduationCap className="h-4 w-4" />My Training</div>
          {d.training.length === 0 ? <p className="text-sm text-muted-foreground">No training assigned.</p> : d.training.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b py-2 text-sm last:border-0"><span>{t.title}</span><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${t.status==='completed'?'bg-[hsl(var(--success))] text-white':t.status==='overdue'?'bg-[hsl(var(--danger))] text-white':'bg-muted'}`}>{t.status}</span></div>
          ))}
        </Card>
        <Card className="p-5">
          <div className="mb-3 flex items-center gap-2 font-display font-semibold"><Package className="h-4 w-4" />Recent Violations</div>
          {d.violations.length === 0 ? <p className="text-sm text-muted-foreground">No violations on record. Keep it up!</p> : d.violations.map((v) => (
            <div key={v.id} className="border-b py-2 text-sm last:border-0"><div className="flex justify-between"><span className="font-medium">{v.description}</span><span className="text-xs text-muted-foreground">{v.date ? new Date(v.date).toLocaleDateString() : ''}</span></div><span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">{v.basic_category}</span></div>
          ))}
        </Card>
      </div>
    </AppShell>
  );
}
